import { zodResolver } from '@hookform/resolvers/zod';
import { Close } from '@mui/icons-material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    useDeclineBookingSetMenuMutation,
    useGetIndividualBookingSetMenuQuery,
    useGetProfileCompanyInfoQuery,
    useUpdateBookingSetMenuMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import {
    camelCaseToTitleCase,
    formatCurrency,
    formatTime,
} from '../../../../shared/utils';
import { Status } from '../../../enums/contractor';
import { ContractorRole, OwnerRole } from '../../../enums/users';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormDateFilter from '../../../shared/FormDateFilter';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';
import { ModalProps } from '../../../types/modal';
import { BookingSetMenu } from '../../../types/set-menu';
import { TimeArray } from '../../Experiences/Owner/Property/TimeArray';
import {
    FormField,
    getStatusSx,
    NavigationControls,
} from '../Jobs/ContractorJobPreview';
import { useSearchParams } from 'react-router-dom';

interface JobPreviewProps extends ModalProps {
    id: number | null;
    jobIds?: { id: number; providerType: ProviderType }[];
    initialIndex?: number;
    onNavigate?: (nextIndex: number) => void;
}

const Schema = z.object({
    provider: z.string(),
    ownerNotes: z.string(),
    guestNotes: z.string(),
    jobDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
});

type FormValues = z.infer<typeof Schema>;

const DEFAULT_MAP_CENTER = { lat: -33.865143, lng: 151.2099 };

const JobMap = ({ experience }: { experience: BookingSetMenu['module'] }) => {
    const [marker, setMarker] = useState<google.maps.LatLngLiteral | null>(
        null,
    );

    const address = `${experience?.streetAddress}, ${experience?.suburb}, ${experience?.state}`;

    const fetchMarker = async () => {
        if (!window.google) return;

        const { Geocoder } = (await google.maps.importLibrary(
            'geocoding',
        )) as google.maps.GeocodingLibrary;

        const geocoder = new Geocoder();

        geocoder
            .geocode({ address })
            .then(({ results }) => {
                const location = results[0].geometry.location;
                setMarker({ lat: location.lat(), lng: location.lng() });
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (experience) fetchMarker();
    }, [experience]);

    const center = marker ?? DEFAULT_MAP_CENTER;

    return (
        <Map
            disableDefaultUI
            center={center}
            zoom={14}
            style={{ height: 150 }}
            clickableIcons={false}
            onClick={() =>
                window.open(
                    `https://google.com/maps/place/${address}`,
                    '_blank',
                )
            }
            draggableCursor='pointer'
        >
            {marker && <Marker position={marker} />}
        </Map>
    );
};

const JobMetaCard = ({
    job,
    address,
}: {
    job: BookingSetMenu;
    address: string;
}) => (
    <Card variant='outlined'>
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <JobMap experience={job?.module} />
        </APIProvider>

        <CardContent>
            <Stack spacing={1.5}>
                <Box>
                    <Typography
                        variant='body3'
                        component='span'
                        sx={{ lineHeight: 1.2, fontWeight: 700 }}
                    >
                        Address:
                    </Typography>
                    <Typography
                        variant='body3'
                        component='span'
                        sx={{ ml: 0.5 }}
                    >
                        {address}
                    </Typography>
                </Box>

                {job.booking.guestCompany && (
                    <>
                        <Box>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ lineHeight: 1.2, fontWeight: 700 }}
                            >
                                No. of Guests:
                            </Typography>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ ml: 0.5 }}
                            >
                                {job.quantity} for Reservation #{job.booking.id}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ lineHeight: 1.2, fontWeight: 700 }}
                            >
                                Check-in:
                            </Typography>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ ml: 0.5 }}
                            >
                                {new Date(
                                    job.booking.startDate,
                                ).toLocaleDateString('en-AU')}{' '}
                                {formatTime(job.booking.checkinTime)}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ lineHeight: 1.2, fontWeight: 700 }}
                            >
                                Checkout:
                            </Typography>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ ml: 0.5 }}
                            >
                                {new Date(
                                    job.booking.endDate,
                                ).toLocaleDateString('en-AU')}{' '}
                                {formatTime(job.booking.checkoutTime)}
                            </Typography>
                        </Box>
                    </>
                )}
            </Stack>
        </CardContent>
    </Card>
);

const OwnerJobPreview = ({
    id,
    open,
    onClose,
    jobIds = [],
    initialIndex = 0,
    onNavigate,
}: JobPreviewProps) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const isOwnerAdmin = loggedInUser?.role === OwnerRole.Admin;
    const isContractor = loggedInUser?.role.includes('contractor');
    const isContractorAdmin = loggedInUser?.role === ContractorRole.Admin;

    const [declineOpen, setDeclineOpen] = useState(false);

    const [searchParams] = useSearchParams();
    const providerTypeParam = searchParams.get('provider-type');

    const currentIndex = jobIds.findIndex(
        (job) =>
            job.id === id &&
            (providerTypeParam ? job.providerType === providerTypeParam : true),
    );

    const { data: companyDetails } = useGetProfileCompanyInfoQuery(
        `owners/${loggedInUser?.company}`,
        { skip: !loggedInUser?.company },
    );
    const theme = useTheme();

    const atStart = jobIds.length > 0 ? currentIndex <= 0 : true;
    const atEnd = jobIds.length > 0 ? currentIndex >= jobIds.length - 1 : true;

    const [updateBookingSetMenu] = useUpdateBookingSetMenuMutation();
    const [declineBookingSetMenu] = useDeclineBookingSetMenuMutation();

    const methods = useForm<FormValues>({
        resolver: zodResolver(Schema),
        mode: 'onChange',
    });

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { dirtyFields, isSubmitting, isDirty },
    } = methods;

    const go = (delta: number) => {
        if (jobIds.length === 0) return;
        const nextIndex = currentIndex + delta;
        if (nextIndex < 0 || nextIndex >= jobIds.length) return;
        onNavigate?.(nextIndex);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowRight' && !atEnd) go(1);
        if (e.key === 'ArrowLeft' && !atStart) go(-1);
    };

    const { data: bookingSetMenu } = useGetIndividualBookingSetMenuQuery(
        id as number,
        {
            skip: !id,
        },
    );

    useEffect(() => {
        if (bookingSetMenu)
            reset({
                provider: companyDetails?.name,
                ...bookingSetMenu,
            });
    }, [bookingSetMenu, reset]);

    const address = `${bookingSetMenu?.module.streetAddress}, ${bookingSetMenu?.module.suburb}, ${bookingSetMenu?.module.state}`;

    const handleClose = () => {
        onClose();
    };

    const onSubmit = async (data: FormValues) => {
        try {
            if (bookingSetMenu?.id) {
                await updateBookingSetMenu([bookingSetMenu?.id, data]).unwrap();
            }

            toast.success('Booking Set menu saved successfully');
            onClose();
        } catch {
            toast.error('Error saving set menu');
        }
    };
    const handleDecline = async () => {
        try {
            if (bookingSetMenu?.id) {
                await declineBookingSetMenu(bookingSetMenu.id).unwrap();
            }

            toast.success('Booking Set menu declined successfully');
            onClose();
        } catch {
            toast.error('Error declining booking set menu');
        }
    };

    const chipSx = bookingSetMenu
        ? getStatusSx(bookingSetMenu.status)
        : getStatusSx('default');

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth='lg'
                fullWidth
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: isMobile ? 'stretch' : 'center',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    },
                }}
                onKeyDown={handleKeyDown}
            >
                {!bookingSetMenu ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                        }}
                    >
                        <LoadingComponent />
                    </Box>
                ) : (
                    <FormProvider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit, () =>
                                toast.error(
                                    'Please fix the highlighted fields before saving.',
                                ),
                            )}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                minHeight: 0,
                            }}
                        >
                            {bookingSetMenu && (
                                <>
                                    <DialogTitle>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <EngineeringIcon
                                                    sx={{ opacity: 0.55 }}
                                                    fontSize='small'
                                                />
                                                Job Request #{id}
                                            </Box>
                                            <CustomChip
                                                variant='outlined'
                                                label={camelCaseToTitleCase(
                                                    bookingSetMenu.status,
                                                ).toUpperCase()}
                                                sx={{
                                                    ...chipSx,
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {jobIds.length > 1 &&
                                                (!isMobile ? (
                                                    <NavigationControls
                                                        atStart={atStart}
                                                        atEnd={atEnd}
                                                        onPrev={() => go(-1)}
                                                        onNext={() => go(1)}
                                                    />
                                                ) : (
                                                    <NavigationControls
                                                        atStart={atStart}
                                                        atEnd={atEnd}
                                                        onPrev={() => go(-1)}
                                                        onNext={() => go(1)}
                                                        showLabels={false}
                                                        compact
                                                    />
                                                ))}

                                            <IconButton
                                                onClick={handleClose}
                                                sx={{
                                                    color: 'text.primary',
                                                }}
                                                aria-label='close'
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    </DialogTitle>
                                    <DialogContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={8}>
                                                <Stack spacing={1}>
                                                    <FormField label='Provider'>
                                                        <FormAutocomplete
                                                            name='provider'
                                                            control={control}
                                                            options={[
                                                                {
                                                                    value: companyDetails?.name,
                                                                    label: companyDetails?.name,
                                                                },
                                                            ]}
                                                            disabled={true}
                                                        />
                                                    </FormField>

                                                    <FormField label='Requested By'>
                                                        <FormTextField
                                                            name='booking.guestCompany'
                                                            control={control}
                                                            disabled
                                                        />
                                                    </FormField>

                                                    <FormField label='Date'>
                                                        <FormDateFilter
                                                            name='jobDate'
                                                            control={control}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField label='Start Time'>
                                                        <FormAutocomplete
                                                            name='startTime'
                                                            control={control}
                                                            options={TimeArray}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField label='End Time'>
                                                        <FormAutocomplete
                                                            name='endTime'
                                                            control={control}
                                                            options={TimeArray}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField label='Job Description'>
                                                        <FormTextField
                                                            name='ownerNotes'
                                                            control={control}
                                                            size='small'
                                                            fullWidth
                                                            multiline
                                                            rows={8}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    'bold',
                                                                marginBottom: 0.5,
                                                                fontSize:
                                                                    '16px',
                                                            }}
                                                        >
                                                            Selected Items
                                                        </Typography>

                                                        <Stack
                                                            sx={{
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography
                                                                    sx={{
                                                                        pt: 1,
                                                                        pb: 0.5,
                                                                        fontWeight:
                                                                            'bold',
                                                                        fontSize:
                                                                            '14px',
                                                                    }}
                                                                >
                                                                    {
                                                                        bookingSetMenu
                                                                            .setMenu
                                                                            .name
                                                                    }{' '}
                                                                    Menu
                                                                </Typography>

                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Grid
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            justifyContent:
                                                                                'space-between',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                flex: 1,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    bookingSetMenu
                                                                                        .setMenu
                                                                                        .name
                                                                                }
                                                                            </Typography>

                                                                            {bookingSetMenu
                                                                                .setMenu
                                                                                .description && (
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontStyle:
                                                                                            'italic',
                                                                                        fontSize:
                                                                                            '14px',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        bookingSetMenu
                                                                                            .setMenu
                                                                                            .description
                                                                                    }
                                                                                </Typography>
                                                                            )}

                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    bookingSetMenu.quantity
                                                                                }{' '}
                                                                                ×{' '}
                                                                                {`${formatCurrency(bookingSetMenu.pricePerPerson)} (ex. GST)`}
                                                                            </Typography>
                                                                        </Box>

                                                                        <Typography
                                                                            sx={{
                                                                                fontSize:
                                                                                    '14px',
                                                                            }}
                                                                        >
                                                                            {formatCurrency(
                                                                                bookingSetMenu.total,
                                                                            )}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Box>
                                                            </Box>
                                                        </Stack>

                                                        {bookingSetMenu.guestNotes && (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 0.5,
                                                                    mt: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight:
                                                                            'bold',
                                                                        fontSize:
                                                                            '14px',
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    Notes:
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            '14px',
                                                                    }}
                                                                >
                                                                    {
                                                                        bookingSetMenu.guestNotes
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        <Box sx={{ mt: 1 }}>
                                                            <Divider />
                                                        </Box>

                                                        <Grid
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                mt: 1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight:
                                                                            'bold',
                                                                        fontSize:
                                                                            '14px',
                                                                    }}
                                                                >
                                                                    Total (ex.
                                                                    GST)
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight:
                                                                        'bold',
                                                                    fontSize:
                                                                        '14px',
                                                                }}
                                                            >
                                                                {formatCurrency(
                                                                    bookingSetMenu.total,
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                    </Box>
                                                </Stack>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <Stack spacing={2}>
                                                    <JobMetaCard
                                                        job={bookingSetMenu}
                                                        address={address}
                                                    />

                                                    <Button
                                                        variant='contained'
                                                        color='warning'
                                                        disabled={
                                                            bookingSetMenu.status ===
                                                                Status.Cancelled ||
                                                            bookingSetMenu.status ===
                                                                Status.Completed ||
                                                            bookingSetMenu.status ===
                                                                Status.Declined
                                                        }
                                                        onClick={() =>
                                                            setDeclineOpen(true)
                                                        }
                                                    >
                                                        Decline Job
                                                    </Button>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </DialogContent>
                                </>
                            )}

                            <DialogActions
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 1,
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    spacing={1}
                                >
                                    {jobIds.length > 1 && (
                                        <Typography
                                            component='span'
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: 14,
                                            }}
                                        >
                                            ({currentIndex + 1} of{' '}
                                            {jobIds.length} Jobs)
                                        </Typography>
                                    )}
                                </Stack>

                                <Stack direction='row' spacing={1}>
                                    <Button
                                        onClick={handleClose}
                                        variant='outlined'
                                        color='primary'
                                        sx={{ flex: isMobile ? 1 : undefined }}
                                    >
                                        Close
                                    </Button>

                                    <Button
                                        variant='contained'
                                        color='primary'
                                        type='submit'
                                        disabled={!isDirty || isSubmitting}
                                    >
                                        Save
                                    </Button>
                                </Stack>
                            </DialogActions>
                        </form>
                    </FormProvider>
                )}
            </Dialog>

            <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)}>
                <DialogTitle>
                    Decline Job
                    <IconButton
                        onClick={() => setDeclineOpen(false)}
                        sx={{ color: 'text.primary' }}
                        aria-label='close'
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        <Typography>
                            Are you sure you want to decline this job?
                        </Typography>
                        <Alert severity='warning'>
                            This action cannot be undone
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='outlined'
                        onClick={() => setDeclineOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        color='error'
                        onClick={handleDecline}
                    >
                        Decline
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OwnerJobPreview;
